/**
 * PATH: packages/ui/src/table/sortRules.ts
 * FILE_ID: UI-TABLE-SORTRULES-001
 * PURPOSE: Pure sort-state maths for the shared header - normalise, rank, ARIA.
 * WHY: Kept out of the component so the rules are unit-testable without a DOM,
 *      and so single-sort and multi-sort callers exercise identical logic.
 * RELATED: SortableTableHeader.tsx, types.ts
 */
import type { SortDir, SortRule } from './types'

/**
 * Collapse either supported input shape into one ordered rule array.
 *
 * Callers pass EITHER `rules` (multi-column) OR the flat `activeKey`/`activeDir`
 * pair (single-column). `rules` wins when both are supplied, because a caller
 * that models multi-sort has strictly more information than the flat pair.
 */
export function toSortRules<K extends string>(
  rules: readonly SortRule<K>[] | undefined,
  activeKey: K | null | undefined,
  activeDir: SortDir | undefined,
): readonly SortRule<K>[] {
  if (rules) return rules
  if (!activeKey) return []
  return [{ key: activeKey, dir: activeDir ?? 'asc' }]
}

/** 1-based sort position for a column, or 0 when the column is not sorted. */
export function sortRankOf<K extends string>(
  rules: readonly SortRule<K>[],
  key: K,
): number {
  return rules.findIndex((rule) => rule.key === key) + 1
}

/** The active direction for a column, or null when the column is not sorted. */
export function sortDirOf<K extends string>(
  rules: readonly SortRule<K>[],
  key: K,
): SortDir | null {
  const rank = sortRankOf(rules, key)
  return rank > 0 ? rules[rank - 1].dir : null
}

/**
 * ARIA sort state for a column header.
 *
 * A secondary sort key reports 'other' rather than its own direction: ARIA
 * models one primary sort per table, so claiming 'ascending' on two columns
 * tells a screen reader the table is sorted two ways at once.
 */
export function ariaSortFor<K extends string>(
  rules: readonly SortRule<K>[],
  key: K,
): 'ascending' | 'descending' | 'other' | 'none' {
  const rank = sortRankOf(rules, key)
  if (rank === 0) return 'none'
  if (rank > 1) return 'other'
  return rules[0].dir === 'asc' ? 'ascending' : 'descending'
}

/** Accessible name for the sort control, stating the current state aloud. */
export function sortButtonLabel(label: string, dir: SortDir | null): string {
  if (!dir) return `Sort by ${label}`
  return `Sort by ${label}, currently ${dir === 'asc' ? 'ascending' : 'descending'}`
}
